import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type CheckboxFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import LinkOrCreateRoundModal from '../../../../rounds/components/LinkOrCreateRoundModal.tsx'
import UnlinkOrDeleteRoundModal, {
  type UnlinkOrDeleteChoice
} from '../../../../rounds/components/UnlinkOrDeleteRoundModal.tsx'
import { deleteRound, fetchRoundDeleteImpact } from '../../../../rounds/services/roundsApi.ts'
import type { RoundDeleteImpact } from '../../../../../state_manager/data_models/round_delete_impact.ts'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { useCurrencies } from '../../../../currencies/useCurrencies.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/index.ts'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import { RoundEntryFields } from '../../../../../state_manager/data_models/round_entry.ts'
import { getRoundInformationFields } from '../../../utils/fieldConfigs.ts'
import { fetchDealById } from '../../../services/dealsService.ts'
import { SECTION_ROUND_INFORMATION } from '../../../../../state_manager/constants/strings.ts'
import { addRound, removeRound } from '../../../../../state_manager/state_update_actions/rounds/index.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

interface RoundInformationSectionProps {
  formData?: any
  handleFieldChange?: (key: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  isReadOnly?: boolean
  recordId?: string | number | null
  cardProps?: any
}

export function RoundInformationSection({
  formData,
  handleFieldChange,
  onPatchSuccess,
  isReadOnly = false,
  recordId = null,
  cardProps = {}
}: RoundInformationSectionProps) {
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()
  const { getLookupOptions } = useLookups()
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()

  const data = formData || {}

  const dealId = recordId || data[DealFields.deals__id] || data.id
  const roundId = data[DealFields.rounds__id] || data.round || null

  const fieldOptions = {
    getCurrencyById,
    getCurrencyOptions,
    getLookupOptions
  }

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    const reduxUpdates: Record<string, any> = { ...updates }

    const isRoundFieldUpdate = patchRecordId === roundId && roundId !== null

    let isFundingRoundValue = formData[DealFields.rounds__is_funding_round]
    if ('is_funding_round' in updates) {
      isFundingRoundValue = updates.is_funding_round
      reduxUpdates[DealFields.rounds__is_funding_round] = updates.is_funding_round
      delete reduxUpdates.is_funding_round
    } else if ('deals__is_funding_round' in updates) {
      isFundingRoundValue = updates.deals__is_funding_round
      reduxUpdates[DealFields.rounds__is_funding_round] = updates.deals__is_funding_round
      delete reduxUpdates.deals__is_funding_round
    } else if (DealFields.rounds__is_funding_round in updates) {
      isFundingRoundValue = updates[DealFields.rounds__is_funding_round]
    }

    const roundIdValue = 'round' in updates ? updates.round : formData[DealFields.rounds__id]
    if ('round' in updates) {
      reduxUpdates[DealFields.rounds__id] = updates.round
      delete reduxUpdates.round
    }

    if (isRoundFieldUpdate) {
      const apiFieldNames = [
        'id',
        'raise_amount',
        'pre_money_valuation',
        'post_money_valuation',
        'name',
        'funding_stage',
        'security_type',
        'round_close_date'
      ]
      apiFieldNames.forEach((apiField) => {
        if (apiField in reduxUpdates && !apiField.includes('__')) {
          delete reduxUpdates[apiField]
        }
      })
    }

    const currentToken = getAccessToken(store.getState().auth)
    // Re-read the deal after a tick: the round the user just linked brings its
    // own name, stage, valuations and close date, and this section renders
    // them. (It used to refetch only when no round id came back, which left a
    // freshly linked round showing blank fields.)
    const needsFetch = isFundingRoundValue === true && currentToken && dealId

    if (needsFetch) {
      try {
        const response = await fetchDealById(dealId, currentToken)
        if (response.data && !response.error) {
          const fetchedDeal = response.data
          // The whole record, so the round's own fields land with the link.
          const finalUpdates = {
            ...fetchedDeal,
            [DealFields.rounds__is_funding_round]:
              fetchedDeal[DealFields.rounds__is_funding_round] ?? isFundingRoundValue,
            [DealFields.rounds__id]: fetchedDeal[DealFields.rounds__id] ?? null
          }

          if (onPatchSuccess) {
            onPatchSuccess(dealId, finalUpdates)
          } else {
            dispatch(updateDealRecord({ dealId, updates: finalUpdates }))
          }
          return
        }
      } catch (error) {
        console.error('Error fetching deal after patch:', error)
      }
    }

    if (
      !(DealFields.rounds__is_funding_round in reduxUpdates) &&
      formData[DealFields.rounds__is_funding_round] !== undefined
    ) {
      reduxUpdates[DealFields.rounds__is_funding_round] = formData[DealFields.rounds__is_funding_round]
    }
    if (!(DealFields.rounds__id in reduxUpdates) && formData[DealFields.rounds__id] !== undefined) {
      reduxUpdates[DealFields.rounds__id] = formData[DealFields.rounds__id]
    }

    if (reduxUpdates[DealFields.rounds__is_funding_round] === false) {
      // The round is gone, so its values must go too — otherwise the section
      // keeps showing a raise amount and a valuation for a deleted round.
      reduxUpdates[DealFields.rounds__id] = null
      reduxUpdates[DealFields.rounds__name] = null
      reduxUpdates[DealFields.rounds__funding_stage] = null
      reduxUpdates[DealFields.rounds__security_type] = null
      reduxUpdates[DealFields.rounds__raise_amount] = null
      reduxUpdates[DealFields.rounds__pre_money_valuation] = null
      reduxUpdates[DealFields.rounds__post_money_valuation] = null
      reduxUpdates[DealFields.rounds__round_close_date] = null
      pickedRoundIdRef.current = null
    }

    const targetDealId = isRoundFieldUpdate ? dealId : patchRecordId

    if (onPatchSuccess) {
      onPatchSuccess(targetDealId, reduxUpdates)
    } else {
      dispatch(updateDealRecord({ dealId: targetDealId, updates: reduxUpdates }))
    }
  }

  const [localIsFundingRound, setLocalIsFundingRound] = useState<boolean>(() => {
    const isFundingRoundValue = formData[DealFields.rounds__is_funding_round]
    if (isFundingRoundValue === true || isFundingRoundValue === false) {
      return isFundingRoundValue
    }
    const hasRoundId = formData[DealFields.rounds__id] !== null && formData[DealFields.rounds__id] !== undefined
    return hasRoundId
  })

  useEffect(() => {
    const isFundingRoundValue = formData[DealFields.rounds__is_funding_round]
    if (isFundingRoundValue === true || isFundingRoundValue === false) {
      setLocalIsFundingRound(isFundingRoundValue)
      return
    }
    const hasRoundId = formData[DealFields.rounds__id] !== null && formData[DealFields.rounds__id] !== undefined
    setLocalIsFundingRound(hasRoundId)
  }, [formData])

  const isFundingRound = localIsFundingRound

  const handleRoundHyperlinkClick = useCallback(
    (module: string, roundIdParam: string, title: string) => {
      const roundData = {
        [RoundEntryFields.rounds__id]: roundIdParam,
        [RoundEntryFields.rounds__name]: data[DealFields.rounds__name] || data.round_name || null,
        [RoundEntryFields.rounds__account]: data[DealFields.deals__account] || data.account || null,
        [RoundEntryFields.rounds__funding_stage]: data[DealFields.rounds__funding_stage] || data.funding_stage || null,
        [RoundEntryFields.rounds__currency]: data[DealFields.deals__currency] || data.currency || null,
        [RoundEntryFields.rounds__raise_amount]: data[DealFields.rounds__raise_amount] || data.raise_amount || null,
        [RoundEntryFields.rounds__pre_money_valuation]:
          data[DealFields.rounds__pre_money_valuation] || data.pre_money_valuation || null,
        [RoundEntryFields.rounds__post_money_valuation]:
          data[DealFields.rounds__post_money_valuation] || data.post_money_valuation || null,
        [RoundEntryFields.rounds__security_type]: data[DealFields.rounds__security_type] || data.security_type || null,
        [RoundEntryFields.rounds__round_close_date]:
          data[DealFields.rounds__round_close_date] || data.round_close_date || null
      }

      if (roundIdParam) {
        dispatch(addRound({ round: roundData }))
      }
      openFormInTab(module, roundIdParam, title)
    },
    [dispatch, openFormInTab, data]
  )

  const roundFields = useMemo(() => {
    const fields = getRoundInformationFields(data, fieldOptions, isReadOnly)
    return fields.map((field: any) => {
      if (field.key === DealFields.rounds__name && field.hyperlinked) {
        return {
          ...field,
          onHyperlinkClick: handleRoundHyperlinkClick
        }
      }
      return field
    })
  }, [data, fieldOptions, isReadOnly, handleRoundHyperlinkClick])

  // ── The checkbox is a consequential control, so it asks in both directions ──
  //
  // Ticking it used to make the backend invent a round with no name and no
  // currency; unticking it deleted a real one without a word. Both now go
  // through confirmPatch, which InputManager already honours: resolving false
  // discards the pending edit and restores the box, so Cancel writes nothing.
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false)
  const [unlinkImpact, setUnlinkImpact] = useState<RoundDeleteImpact | null>(null)
  const [unlinkBusy, setUnlinkBusy] = useState(false)
  // The round the user picked or created, handed to the valueTransformer that
  // builds the PATCH body a moment later.
  const pickedRoundIdRef = useRef<string | null>(null)
  const linkResolverRef = useRef<((roundId: string | null) => void) | null>(null)
  const unlinkResolverRef = useRef<((choice: UnlinkOrDeleteChoice) => void) | null>(null)

  const resolveLinkModal = useCallback((roundId: string | null) => {
    setLinkModalOpen(false)
    const resolve = linkResolverRef.current
    linkResolverRef.current = null
    resolve?.(roundId)
  }, [])

  /** The round the deal links NOW, read from the server. The cached record can
   *  predate a link made elsewhere (another tab, the Execution tab, or the
   *  Dataverse sync, which writes PROD directly): trusting it let an untick
   *  skip the Unlink / Delete question over a round the form had never seen
   *  (Optimal sale to Source.ag, 2026-09-23). Falls back to the cached id if
   *  the read fails, so a network blip never blocks the checkbox. */
  const fetchCurrentRoundId = useCallback(async (): Promise<string | null> => {
    const cachedRoundId = formData?.[DealFields.rounds__id] || null
    const currentDealId = formData?.[DealFields.deals__id]
    if (!currentDealId) return cachedRoundId
    const token = getAccessToken(store.getState().auth)
    const { data: freshDeal, error } = await fetchDealById(currentDealId, token)
    if (error || !freshDeal) return cachedRoundId
    return (freshDeal[DealFields.rounds__id] as string | null | undefined) || null
  }, [formData])

  /** Tick: ask which round. An existing link is simply re-affirmed. */
  const confirmTick = useCallback(async (): Promise<boolean> => {
    const existingRoundId = await fetchCurrentRoundId()
    if (existingRoundId) {
      pickedRoundIdRef.current = existingRoundId
      return true
    }
    if (!formData?.[DealFields.deals__account]) {
      showErrorToast('This deal has no company yet — a round belongs to a company')
      return false
    }
    const picked = await new Promise<string | null>((resolve) => {
      linkResolverRef.current = resolve
      setLinkModalOpen(true)
    })
    if (!picked) return false
    pickedRoundIdRef.current = picked
    return true
  }, [formData, fetchCurrentRoundId])

  /** Untick: the deal stops being a funding round either way, but the round is
   *  a company record — so ask whether to keep it or delete it (owner
   *  instruction 2026-09-21). Deleting goes through DELETE /rounds, the one
   *  guarded path; the PATCH that follows only clears the link. */
  const confirmUntick = useCallback(async (): Promise<boolean> => {
    const roundId = await fetchCurrentRoundId()
    if (!roundId) return true // nothing linked, nothing to decide

    const token = getAccessToken(store.getState().auth)
    const { data: impact } = await fetchRoundDeleteImpact(token, roundId)

    const choice = await new Promise<UnlinkOrDeleteChoice>((resolve) => {
      unlinkResolverRef.current = resolve
      setUnlinkImpact(impact)
      setUnlinkModalOpen(true)
    })

    if (choice === 'cancel') {
      setUnlinkModalOpen(false)
      return false
    }

    if (choice === 'delete') {
      setUnlinkBusy(true)
      try {
        const result = await deleteRound(token, roundId)
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete the round')
          setUnlinkModalOpen(false)
          return false
        }
        dispatch(removeRound({ roundId }))
        toast.success('Round deleted')
      } finally {
        setUnlinkBusy(false)
      }
    }

    setUnlinkModalOpen(false)
    return true
  }, [dispatch, fetchCurrentRoundId])

  const checkboxField = useMemo<CheckboxFieldConfig>(
    () => ({
      key: DealFields.rounds__is_funding_round,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: isFundingRound,
      checkboxLabel: 'Is Funding Round',
      editable: !isReadOnly,
      patchEndpoint: 'deals',
      // The checkbox reports its new value through onChange BEFORE the gate
      // resolves, so a declined gate has to put the local state back — else
      // the box stays visibly ticked over a deal that was never changed.
      confirmPatch: async (value: boolean) => {
        const proceed = value ? await confirmTick() : await confirmUntick()
        if (!proceed) setLocalIsFundingRound(!value)
        return proceed
      },
      valueTransformer: (value: boolean, currentRecord: any) => ({
        id: formData?.[DealFields.deals__id],
        // On a tick this is the round the user just chose; on an untick the
        // backend re-reads the link from the record anyway, so a stale id here
        // can no longer cost the deal the wrong round.
        round: value ? pickedRoundIdRef.current || formData?.[DealFields.rounds__id] : formData?.[DealFields.rounds__id],
        is_funding_round: value,
        [DealFields.rounds__is_funding_round]: value
      })
    }),
    [isFundingRound, isReadOnly, formData, confirmTick, confirmUntick]
  )

  return (
    <FormSection
      data-debug='round-information-section'
      data-component='RoundInformationSection'
      title={SECTION_ROUND_INFORMATION}
      titleColor='green'
      {...cardProps}
    >
      <div data-debug='round-information-section-content'>
        <UnlinkOrDeleteRoundModal
          open={unlinkModalOpen}
          impact={unlinkImpact}
          busy={unlinkBusy}
          onChoose={(choice) => {
            const resolve = unlinkResolverRef.current
            unlinkResolverRef.current = null
            resolve?.(choice)
          }}
        />
        <LinkOrCreateRoundModal
          open={linkModalOpen}
          companyId={data[DealFields.deals__account] || data[DealFields.accounts__id] || null}
          companyName={data[DealFields.accounts__name] || null}
          defaultCurrencyId={data[DealFields.deals__currency] || null}
          onPicked={(roundId) => resolveLinkModal(roundId)}
          onCancel={() => resolveLinkModal(null)}
        />
        <div className='mb-3'>
          <InputManager
            inputMode='edit'
            labelPosition='inline'
            fields={[checkboxField]}
            onChange={(key: string, value: any) => {
              if (key === DealFields.rounds__is_funding_round) {
                setLocalIsFundingRound(value)
              }
              handleFieldChange?.(key, value)
            }}
            enableAutoPatch={!isReadOnly && !!dealId}
            patchId={dealId}
            recordId={dealId}
            onPatchSuccess={handlePatchSuccessLocal}
            className='round-information-checkbox'
          />
        </div>

        {isFundingRound && (
          <InputManager
            inputMode='read'
            labelPosition='inline'
            fields={roundFields}
            onChange={(key: string, value: any) => handleFieldChange?.(key, value)}
            enableAutoPatch={!isReadOnly && !!roundId}
            patchId={roundId}
            recordId={roundId}
            onPatchSuccess={handlePatchSuccessLocal}
            className='round-information-section-fields'
          />
        )}
      </div>
    </FormSection>
  )
}
